import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';

const SITE_URL = 'https://ofalycee.fr';
const OG_IMAGE = '/og-share.png';

const seoData = {
  '/': {
    titleFr: 'OFA France · Scolarité américaine en France | U.S. High School Diploma',
    titleEn: 'OFA France · American Education in France | U.S. High School Diploma',
    descFr: 'OFA France propose une scolarité américaine accréditée COGNIA en parallèle de l\'école française, de la Grande Section à la Terminale. Obtenez un U.S. High School Diploma 100% en ligne.',
    descEn: 'OFA France offers a COGNIA-accredited American education alongside French schooling, from Kindergarten to 12th grade. Earn a U.S. High School Diploma 100% online.',
    keywordsFr: 'scolarité américaine france, diplôme américain france, école bilingue en ligne, high school diploma france, OFA France, COGNIA accrédité, programme américain france',
    keywordsEn: 'american education france, american diploma france, bilingual school online, high school diploma france, OFA France, COGNIA accredited, american program france',
  },
  '/programs': {
    titleFr: 'Nos Programmes · OFA France | Cursus américain bilingue K-12',
    titleEn: 'Our Programs · OFA France | Bilingual American Curriculum K-12',
    descFr: 'Découvrez les programmes OFA France : ELA, US History, débat, écriture académique. Deux parcours adaptés — Rester Bilingue (Fluent Track) et Devenir Bilingue (Bridge Track).',
    descEn: 'Discover OFA France programs: ELA, US History, debate, academic writing. Two adapted tracks — Stay Bilingual (Fluent Track) and Become Bilingual (Bridge Track).',
    keywordsFr: 'programme américain en ligne, ELA france, cursus bilingue, anglais académique, cours américain france',
    keywordsEn: 'american program online, ELA france, bilingual curriculum, academic english, american courses france',
  },
  '/how-it-works': {
    titleFr: 'Comment ça fonctionne · OFA France | École américaine en ligne',
    titleEn: 'How It Works · OFA France | American Online School',
    descFr: 'Cours en direct avec des professeurs américains certifiés, classes de 4-8 élèves, 100% en ligne. Découvrez le fonctionnement d\'OFA France au quotidien.',
    descEn: 'Live classes with certified American teachers, 4-8 students per class, 100% online. Discover how OFA France works on a daily basis.',
    keywordsFr: 'école en ligne france, cours en direct anglais, professeurs américains, petites classes en ligne',
    keywordsEn: 'online school france, live english classes, american teachers, small online classes',
  },
  '/difference': {
    titleFr: 'La Différence OFA · OFA France | Pourquoi nous choisir',
    titleEn: 'The OFA Difference · OFA France | Why Choose Us',
    descFr: 'OFA France n\'est pas un cours d\'anglais. C\'est un parcours académique structuré avec accréditation COGNIA, diplôme reconnu et classes à effectifs réduits.',
    descEn: 'OFA France is not an English course. It\'s a structured academic pathway with COGNIA accreditation, recognized diploma and small class sizes.',
    keywordsFr: 'OFA vs école internationale, avantage bilingue, différence OFA, école accréditée france',
    keywordsEn: 'OFA vs international school, bilingual advantage, OFA difference, accredited school france',
  },
  '/returning-families': {
    titleFr: 'Quel parcours pour votre enfant · OFA France | Fluent vs Bridge Track',
    titleEn: 'Which Track for Your Child · OFA France | Fluent vs Bridge Track',
    descFr: 'Comparez les parcours OFA Fluent Track (Rester Bilingue) et Bridge Track (Devenir Bilingue). Trouvez le programme adapté au niveau d\'anglais de votre enfant.',
    descEn: 'Compare OFA Fluent Track (Stay Bilingual) and Bridge Track (Become Bilingual). Find the program suited to your child\'s English level.',
    keywordsFr: 'rester bilingue, devenir bilingue, expatrié france anglais, enfant bilingue programme, parcours adapté anglais',
    keywordsEn: 'stay bilingual, become bilingual, expat france english, bilingual child program, adapted english track',
  },
  '/diploma': {
    titleFr: 'Diplôme & Parcours · OFA France | U.S. High School Diploma accrédité COGNIA',
    titleEn: 'Diploma & Pathways · OFA France | COGNIA Accredited U.S. High School Diploma',
    descFr: 'Obtenez un U.S. High School Diploma accrédité COGNIA en parallèle du Baccalauréat français. 24 crédits, reconnaissance internationale, accès aux universités mondiales.',
    descEn: 'Earn a COGNIA-accredited U.S. High School Diploma alongside the French Baccalauréat. 24 credits, international recognition, access to global universities.',
    keywordsFr: 'high school diploma france, COGNIA accréditation, diplôme américain accrédité, double diplôme france usa',
    keywordsEn: 'high school diploma france, COGNIA accreditation, accredited american diploma, dual diploma france usa',
  },
  '/admissions': {
    titleFr: 'Admissions · OFA France | Inscription scolarité américaine',
    titleEn: 'Admissions · OFA France | American Education Enrollment',
    descFr: 'Processus d\'admission OFA France en 3 étapes : demande, évaluation du niveau d\'anglais, inscription. Rentrée septembre 2026. Inscriptions ouvertes.',
    descEn: 'OFA France admission process in 3 steps: application, English level assessment, enrollment. School year starts September 2026. Enrollment open.',
    keywordsFr: 'inscription école bilingue, admission OFA France, inscription programme américain, rentrée 2026',
    keywordsEn: 'bilingual school enrollment, OFA France admission, american program registration, school year 2026',
  },
  '/tuition': {
    titleFr: 'Frais de scolarité · OFA France | Tarifs 2026-2027',
    titleEn: 'Tuition & Fees · OFA France | 2026-2027 Pricing',
    descFr: 'Tarifs OFA France 2026-2027 : à partir de 2 090€/an (Élémentaire) et 2 690€/an (Collège-Lycée). Facilités de paiement et réductions fratries disponibles.',
    descEn: 'OFA France tuition 2026-2027: from €2,090/year (Elementary) and €2,690/year (Middle-High School). Payment plans and sibling discounts available.',
    keywordsFr: 'tarif école bilingue, prix scolarité américaine france, coût programme bilingue, frais OFA France',
    keywordsEn: 'bilingual school tuition, american education cost france, bilingual program price, OFA France fees',
  },
  '/about': {
    titleFr: 'À Propos · OFA France | Notre Histoire et Équipe',
    titleEn: 'About · OFA France | Our History and Team',
    descFr: 'OFA France est né du succès d\'OFA Lycée. Plus de 800 élèves dans le monde, 100% de réussite au bac, accréditation COGNIA. Découvrez notre histoire et notre équipe.',
    descEn: 'OFA France was born from the success of OFA Lycée. Over 800 students worldwide, 100% bac pass rate, COGNIA accreditation. Discover our history and team.',
    keywordsFr: 'OFA Lycée France, école franco-américaine, histoire OFA, équipe OFA France',
    keywordsEn: 'OFA Lycée France, french-american school, OFA history, OFA France team',
  },
  '/partnerships': {
    titleFr: 'Partenariats · OFA France | Écoles partenaires',
    titleEn: 'Partnerships · OFA France | Partner Schools',
    descFr: 'Devenez école partenaire OFA France. Intégrez un programme américain accrédité COGNIA dans votre établissement et offrez un parcours bilingue à vos élèves.',
    descEn: 'Become an OFA France partner school. Integrate a COGNIA-accredited American program into your institution and offer a bilingual pathway to your students.',
    keywordsFr: 'partenariat école bilingue, programme américain établissement, partenaire OFA France',
    keywordsEn: 'bilingual school partnership, american program institution, OFA France partner',
  },
  '/contact': {
    titleFr: 'Contact · OFA France | Contactez-nous',
    titleEn: 'Contact · OFA France | Get in Touch',
    descFr: 'Contactez OFA France pour toute question sur nos programmes, les admissions ou les tarifs. Réponse sous 48h. Email : info@ofalycee.fr',
    descEn: 'Contact OFA France for any questions about our programs, admissions or tuition. Response within 48h. Email: info@ofalycee.fr',
    keywordsFr: 'contact OFA France, question école bilingue, renseignement programme américain',
    keywordsEn: 'contact OFA France, bilingual school question, american program information',
  },
  '/events': {
    titleFr: 'Événements · OFA France | Portes ouvertes virtuelles',
    titleEn: 'Events · OFA France | Virtual Open Days',
    descFr: 'Participez aux portes ouvertes virtuelles d\'OFA France. Rencontrez notre équipe, posez vos questions et découvrez nos programmes en direct.',
    descEn: 'Attend OFA France virtual open days. Meet our team, ask your questions and discover our programs live.',
    keywordsFr: 'portes ouvertes OFA France, événement école bilingue, journée portes ouvertes en ligne',
    keywordsEn: 'OFA France open days, bilingual school event, online open house',
  },
  '/privacy': {
    titleFr: 'Politique de Confidentialité · OFA France',
    titleEn: 'Privacy Policy · OFA France',
    descFr: 'Politique de confidentialité d\'OFA France. Informations sur la collecte, le traitement et la protection de vos données personnelles conformément au RGPD.',
    descEn: 'OFA France privacy policy. Information about the collection, processing and protection of your personal data in accordance with GDPR.',
    keywordsFr: 'politique confidentialité OFA France, RGPD, protection données',
    keywordsEn: 'privacy policy OFA France, GDPR, data protection',
  },
  '/faq': {
    titleFr: 'FAQ · OFA France | Questions fréquentes sur la scolarité américaine en France',
    titleEn: 'FAQ · OFA France | Frequently Asked Questions about American Education in France',
    descFr: 'Toutes les réponses à vos questions sur OFA France : programme américain, cours en ligne, diplôme U.S. High School Diploma, admission, tarifs, accréditation COGNIA.',
    descEn: 'All answers to your questions about OFA France: American program, online classes, U.S. High School Diploma, admission, tuition, COGNIA accreditation.',
    keywordsFr: 'FAQ OFA France, questions école bilingue, high school diploma france questions, admission OFA France, tarifs école américaine',
    keywordsEn: 'FAQ OFA France, bilingual school questions, high school diploma france questions, OFA France admission, american school tuition',
  },
};

export default function SEO({ path }) {
  const { language } = useLanguage();
  const data = seoData[path] || seoData['/'];

  const title = language === 'fr' ? data.titleFr : data.titleEn;
  const description = language === 'fr' ? data.descFr : data.descEn;
  const keywords = language === 'fr' ? data.keywordsFr : data.keywordsEn;
  const canonical = `${SITE_URL}${path === '/' ? '' : path}`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonical} />
      <meta name="language" content={language === 'fr' ? 'French' : 'English'} />
      <html lang={language} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={`${SITE_URL}${OG_IMAGE}`} />
      <meta property="og:site_name" content="OFA France" />
      <meta property="og:locale" content={language === 'fr' ? 'fr_FR' : 'en_US'} />

      {/* Hreflang - bilingual */}
      <link rel="alternate" hrefLang="fr" href={canonical} />
      <link rel="alternate" hrefLang="en" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${SITE_URL}${OG_IMAGE}`} />
    </Helmet>
  );
}
